import clsx from 'clsx';
import React, {
  type ComponentPropsWithoutRef,
  type CSSProperties,
  forwardRef,
  type KeyboardEvent,
  type ReactNode,
  useId,
} from 'react';

import styles from './FlowChart.module.scss';

export const FLOW_CHART_NODE_WIDTH = 104;
export const FLOW_CHART_NODE_HEIGHT = 40;

export type FlowChartNode = {
  id: string;
  label: string;
  /** Small type/kind label rendered above the main label. */
  sublabel?: string;
  /** Extra detail text rendered below the main label. */
  detail?: string;
  x: number;
  y: number;
  /** CSS color value applied as the `--accent` custom property for this node. */
  accent?: string;
  isHighlighted?: boolean;
  /** Text shown inside the highlight pill when `isHighlighted` is true. */
  highlightLabel?: string;
  /** When true, renders the node at reduced opacity. */
  isDimmed?: boolean;
  /** When provided, the node becomes interactive (click + Enter/Space). */
  onPress?: () => void;
};

export type FlowChartEdge = {
  id: string;
  from: string;
  to: string;
  label?: string;
  isSelected?: boolean;
  isDownstream?: boolean;
  /** When true, renders the edge at reduced opacity. */
  isDimmed?: boolean;
  /** When provided, a wider transparent hit area is rendered for click-to-select. */
  onPress?: () => void;
};

export type FlowChartLane = {
  id: string;
  label: string;
  x: number;
  width: number;
  /** CSS color value applied as the `--accent` custom property for this lane. */
  accent?: string;
};

export type FlowChartGraph = {
  nodes: FlowChartNode[];
  edges: FlowChartEdge[];
  width: number;
  height: number;
};

export type FlowChartViewport = {
  x: number;
  y: number;
  scale: number;
};

export type FlowChartProps = Omit<
  ComponentPropsWithoutRef<'svg'>,
  'children' | 'viewBox' | 'role'
> & {
  graph: FlowChartGraph;
  /** Node bounding-box width in SVG units. Defaults to `FLOW_CHART_NODE_WIDTH`. */
  nodeWidth?: number;
  /** Node bounding-box height in SVG units. Defaults to `FLOW_CHART_NODE_HEIGHT`. */
  nodeHeight?: number;
  /** When omitted, edge labels are shown when the graph has ≤ 7 edges. */
  showEdgeLabels?: boolean;
  /**
   * When provided, all content (children, edges, nodes) is wrapped in a
   * `<g transform="translate(x,y) scale(s)">` for external zoom/pan control.
   */
  viewport?: FlowChartViewport;
  'aria-label'?: string;
  /**
   * Controls how the accent color is shown on each node.
   * - `"dot"` (default) — small circle + sublabel text in the top-left corner.
   * - `"stripe"` — full-height colored bar on the left edge + sublabel text.
   */
  nodeAccentVariant?: 'dot' | 'stripe';
  /**
   * Visual treatment for the chart.
   * - `"default"` — compact generic flow chart.
   * - `"lineage"` — larger, provenance-friendly chart with softer lanes/nodes.
   */
  variant?: 'default' | 'lineage';
  /** Vertical background bands rendered behind edges and nodes. */
  lanes?: FlowChartLane[];
  /** Rendered inside the SVG before edges and nodes — use for background decorations. */
  children?: ReactNode;
};

type FlowChartNodeStyle = CSSProperties & Record<'--accent', string>;

/** Truncates a label to fit inside the default node width. */
export const truncateFlowChartLabel = (value: string): string =>
  value.length > 22 ? `${value.slice(0, 19)}...` : value;

/**
 * BFS traversal — returns the set of all node IDs reachable downstream
 * from `startNodeId` following directed edges.
 */
export const getDownstreamNodeIds = (
  edges: Array<{ from: string; to: string }>,
  startNodeId: string
): Set<string> => {
  const adjacency = edges.reduce<Map<string, string[]>>((acc, edge) => {
    const targets = acc.get(edge.from) ?? [];
    targets.push(edge.to);
    acc.set(edge.from, targets);
    return acc;
  }, new Map());
  const downstream = new Set<string>();
  const queue = [startNodeId];

  while (queue.length) {
    const current = queue.shift();

    if (!current || downstream.has(current)) {
      continue;
    }

    downstream.add(current);
    queue.push(...(adjacency.get(current) ?? []));
  }

  return downstream;
};

/**
 * Returns the set of edge IDs whose `from` node is reachable downstream
 * from `startNodeId`. Pairs with `getDownstreamNodeIds` for highlighting.
 */
export const getDownstreamEdgeIds = (
  edges: Array<{ from: string; id: string; to: string }>,
  startNodeId: string
): Set<string> => {
  const downstreamNodes = getDownstreamNodeIds(edges, startNodeId);

  return new Set(
    edges.filter(edge => downstreamNodes.has(edge.from)).map(edge => edge.id)
  );
};

/**
 * Converts a pointer/mouse event position into SVG coordinate space.
 * Pass `{ width: graph.width, height: graph.height }` from your `FlowChartGraph`.
 * Use this when implementing pan/zoom with the `viewport` prop.
 */
export const svgPointFromEvent = (
  event: Pick<MouseEvent | PointerEvent, 'clientX' | 'clientY'>,
  svg: SVGSVGElement,
  graphSize: { width: number; height: number }
) => {
  const rect = svg.getBoundingClientRect();

  return {
    x: ((event.clientX - rect.left) / rect.width) * graphSize.width,
    y: ((event.clientY - rect.top) / rect.height) * graphSize.height,
  };
};

const cubicEdgePath = (
  from: FlowChartNode,
  to: FlowChartNode,
  nodeWidth: number
): string => {
  const startX = from.x + nodeWidth / 2;
  const endX = to.x - nodeWidth / 2;
  const bend = Math.max(28, (endX - startX) * 0.52);
  return `M ${startX} ${from.y} C ${startX + bend} ${from.y}, ${endX - bend} ${to.y}, ${endX} ${to.y}`;
};

type NodeSublabelProps = {
  hw: number;
  hh: number;
  sublabel: string;
  variant: 'dot' | 'stripe';
};
const NodeSublabel = ({ hw, hh, sublabel, variant }: NodeSublabelProps) => (
  <>
    {variant === 'stripe' ? (
      <rect
        className={styles.nodeStripe}
        height={hh * 2}
        rx="3"
        width={4}
        x={-hw}
        y={-hh}
      />
    ) : (
      <circle className={styles.nodeDot} cx={-hw + 6} cy={-hh + 12} r="3" />
    )}
    <text className={styles.nodeSublabel} x={-hw + 11} y={-hh + 15}>
      {sublabel}
    </text>
  </>
);

type NodeHighlightPillProps = { hw: number; hh: number; label: string };
const NodeHighlightPill = ({ hw, hh, label }: NodeHighlightPillProps) => {
  const width = Math.max(34, label.length * 5.5 + 12);
  const x = hw - width - 6;

  return (
    <>
      <rect
        className={styles.nodeHighlightPill}
        height="12"
        rx="6"
        width={width}
        x={x}
        y={-hh + 6}
      />
      <text
        className={styles.nodeHighlightLabel}
        x={x + width / 2}
        y={-hh + 15}
      >
        {label}
      </text>
    </>
  );
};

const nodeLabelY = (
  hh: number,
  hasSublabel: boolean,
  hasDetail: boolean
): number => {
  if (hasSublabel) {
    return hasDetail ? 2 : hh - 9;
  }
  return hasDetail ? -4 : 4;
};

type NodePressHandlers = {
  onClick: () => void;
  onKeyDown: (event: KeyboardEvent<SVGGElement>) => void;
  role: 'button';
  tabIndex: 0;
};

const buildNodePressHandlers = (onPress: () => void): NodePressHandlers => ({
  onClick: onPress,
  onKeyDown: (event: KeyboardEvent<SVGGElement>) => {
    if (event.key !== 'Enter' && event.key !== ' ') {
      return;
    }
    event.preventDefault();
    onPress();
  },
  role: 'button',
  tabIndex: 0,
});

type NodeItemProps = {
  node: FlowChartNode;
  nodeWidth: number;
  nodeHeight: number;
  accentVariant: 'dot' | 'stripe';
};

const NodeItem = ({
  node,
  nodeWidth,
  nodeHeight,
  accentVariant,
}: NodeItemProps) => {
  const hw = nodeWidth / 2;
  const hh = nodeHeight / 2;
  const nodeStyle: FlowChartNodeStyle | undefined = node.accent
    ? { '--accent': node.accent }
    : undefined;
  const hasSublabel = Boolean(node.sublabel);
  const hasDetail = Boolean(node.detail);
  const labelY = nodeLabelY(hh, hasSublabel, hasDetail);
  const pressHandlers = node.onPress
    ? buildNodePressHandlers(node.onPress)
    : undefined;

  return (
    <g
      {...pressHandlers}
      className={styles.node}
      data-clickable={pressHandlers ? true : undefined}
      data-dimmed={node.isDimmed || undefined}
      data-highlighted={node.isHighlighted || undefined}
      style={nodeStyle}
      transform={`translate(${node.x}, ${node.y})`}
    >
      <title>
        {node.label}
        {node.detail ? ` — ${node.detail}` : ''}
      </title>
      {node.isHighlighted ? (
        <rect
          className={styles.nodeHalo}
          height={nodeHeight + 10}
          rx="10"
          width={nodeWidth + 10}
          x={-(nodeWidth + 10) / 2}
          y={-(nodeHeight + 10) / 2}
        />
      ) : null}
      <rect
        className={styles.nodeRect}
        height={nodeHeight}
        rx="7"
        width={nodeWidth}
        x={-hw}
        y={-hh}
      />
      {hasSublabel && node.sublabel ? (
        <NodeSublabel
          hw={hw}
          hh={hh}
          sublabel={node.sublabel}
          variant={accentVariant}
        />
      ) : null}
      <text className={styles.nodeLabel} x={-hw + 11} y={labelY}>
        {truncateFlowChartLabel(node.label)}
      </text>
      {hasDetail ? (
        <text className={styles.nodeDetail} x={-hw + 11} y={hh - 9}>
          {node.detail}
        </text>
      ) : null}
      {node.isHighlighted && node.highlightLabel ? (
        <NodeHighlightPill hw={hw} hh={hh} label={node.highlightLabel} />
      ) : null}
    </g>
  );
};

type EdgeItemProps = {
  edge: FlowChartEdge;
  from: FlowChartNode;
  to: FlowChartNode;
  nodeWidth: number;
  markerId: string;
  showLabel: boolean;
};

const EdgeItem = ({
  edge,
  from,
  to,
  nodeWidth,
  markerId,
  showLabel,
}: EdgeItemProps) => {
  const pathD = cubicEdgePath(from, to, nodeWidth);

  const handleEdgePress = edge.onPress;
  const handleEdgeKeyDown = handleEdgePress
    ? (event: KeyboardEvent<SVGPathElement>) => {
        if (event.key !== 'Enter' && event.key !== ' ') {
          return;
        }
        event.preventDefault();
        handleEdgePress();
      }
    : undefined;

  return (
    <g
      className={styles.edgeGroup}
      data-dimmed={edge.isDimmed || undefined}
      data-downstream={edge.isDownstream || undefined}
      data-selected={edge.isSelected || undefined}
    >
      <path className={styles.edge} d={pathD} markerEnd={`url(#${markerId})`} />
      {showLabel && edge.label ? (
        <text
          className={styles.edgeLabel}
          x={(from.x + to.x) / 2}
          y={(from.y + to.y) / 2 - 6}
        >
          {edge.label}
        </text>
      ) : null}
      {handleEdgePress ? (
        <path
          aria-label={edge.label ?? `${from.label} to ${to.label}`}
          className={styles.edgeHitArea}
          d={pathD}
          onClick={event => {
            event.stopPropagation();
            handleEdgePress();
          }}
          onKeyDown={handleEdgeKeyDown}
          role="button"
          tabIndex={0}
        />
      ) : null}
    </g>
  );
};

type LaneItemProps = {
  lane: FlowChartLane;
  height: number;
};

const LaneItem = ({ lane, height }: LaneItemProps) => {
  const laneStyle: FlowChartNodeStyle | undefined = lane.accent
    ? { '--accent': lane.accent }
    : undefined;

  return (
    <g className={styles.lane} style={laneStyle}>
      <rect
        className={styles.laneRect}
        height={Math.max(0, height - 18)}
        rx="10"
        width={lane.width}
        x={lane.x}
        y="9"
      />
      <text className={styles.laneLabel} x={lane.x + 10} y="25">
        {lane.label}
      </text>
    </g>
  );
};

/**
 * Generic SVG flow-chart that renders positioned nodes connected by
 * cubic-bezier edges. Node layout must be pre-computed by the caller.
 *
 * Pass background decorations (e.g. layer bands) as `children` — they are
 * rendered inside the viewport group, before edges and nodes.
 *
 * Pass a `viewport` prop to enable external zoom/pan control: all content
 * is wrapped in `<g transform="translate(x,y) scale(s)">`.
 *
 * Forward a `ref` to the root `<svg>` to attach a non-passive wheel listener.
 *
 * Any additional SVG element props (e.g. `onPointerDown`, `onClick`,
 * `data-panning`) are forwarded to the root `<svg>`.
 */
export const FlowChart = forwardRef<SVGSVGElement, FlowChartProps>(
  (
    {
      graph,
      nodeWidth = FLOW_CHART_NODE_WIDTH,
      nodeHeight = FLOW_CHART_NODE_HEIGHT,
      showEdgeLabels,
      nodeAccentVariant = 'dot',
      variant = 'default',
      lanes,
      viewport,
      className,
      'aria-label': ariaLabel,
      children,
      ...svgProps
    },
    ref
  ) => {
    const markerId = useId();
    const nodeById = new Map(graph.nodes.map(n => [n.id, n]));
    const shouldShowEdgeLabels =
      showEdgeLabels !== undefined ? showEdgeLabels : graph.edges.length <= 7;

    const content = (
      <>
        {lanes?.map(lane => (
          <LaneItem height={graph.height} key={lane.id} lane={lane} />
        ))}
        {children}
        {graph.edges.map(edge => {
          const from = nodeById.get(edge.from);
          const to = nodeById.get(edge.to);
          if (!from || !to) {
            return null;
          }
          return (
            <EdgeItem
              edge={edge}
              from={from}
              key={edge.id}
              markerId={markerId}
              nodeWidth={nodeWidth}
              showLabel={shouldShowEdgeLabels}
              to={to}
            />
          );
        })}
        {graph.nodes.map(node => (
          <NodeItem
            key={node.id}
            accentVariant={nodeAccentVariant}
            node={node}
            nodeHeight={nodeHeight}
            nodeWidth={nodeWidth}
          />
        ))}
      </>
    );

    return (
      <svg
        {...svgProps}
        ref={ref}
        aria-hidden={ariaLabel ? undefined : true}
        aria-label={ariaLabel}
        className={clsx(styles.root, className)}
        data-variant={variant}
        role={ariaLabel ? 'img' : undefined}
        viewBox={`0 0 ${graph.width} ${graph.height}`}
      >
        <defs>
          <marker
            id={markerId}
            markerHeight="7"
            markerWidth="7"
            orient="auto"
            refX="7"
            refY="3.5"
          >
            <path className={styles.arrowPath} d="M0,0 L7,3.5 L0,7 Z" />
          </marker>
        </defs>
        {viewport ? (
          <g
            transform={`translate(${viewport.x} ${viewport.y}) scale(${viewport.scale})`}
          >
            {content}
          </g>
        ) : (
          content
        )}
      </svg>
    );
  }
);

FlowChart.displayName = 'FlowChart';

/**
 * Produces a typed inline `style` object that sets the `--accent` CSS custom
 * property. Use whenever a component reads `var(--accent)` from its stylesheet.
 */
export const accentStyle = (
  accent: string
): CSSProperties & Record<'--accent', string> => ({ '--accent': accent });
